import React from "react";
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { verifyOtpSchema } from "../../helpers/validations/forgotPasswordValidation";
import { adminVerifyOtp, parentVerifyOtp, studentVerifyOtp, employeeVerifyOtp } from "../../api/login/loginService";
import Button from "../Button/Button";
import { useSnackbar } from "../MUIComponent/FeedbackComponent/SnackBar/SnackbarContext";
import { useAuth } from "../../hooks/useAuth";
import { useParentKidAuth } from "../../hooks/parentKidUseAuth";
import { Roles } from "../../constants/params/constants";
import CustomeFormInputField from "../FormFields/CustomeFormInputField";
import { useSelector } from "react-redux";
import { encryptPasswordAES } from "../../services/EncriptPasswordAES";
import { useTranslation } from 'react-i18next';

const PasswordOtpVerify = ({ formData, setStep, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();

  const { schoolProfileData } = useSelector(state => state.schoolProfile);
  const { parentkidUser } = useParentKidAuth();

  const User =
    parentkidUser?.roleName === Roles[3].RoleName
      ? parentkidUser
      : user;
  const isEmployee = User?.roleName === Roles[2].RoleName || User?.roleName === Roles[4].RoleName || User?.roleName === Roles[7].RoleName ;
  const isParent = User?.roleName === Roles[3].RoleName;
  const isStudent = User?.roleName === Roles[1].RoleName;
  ;

  const {

    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(verifyOtpSchema),
  });
  const getUserNameWithSchool = (registrationNumber) => {
    const shortName = schoolProfileData?.shortName || "";
    return shortName
      ? `${shortName}${registrationNumber}`
      : registrationNumber;
  };

  const onSubmit = async (data) => {
    try {
      let payload;
      let response;

      const encryptedPassword = encryptPasswordAES(formData.newPassword);
      if (isEmployee || isStudent) {
        payload = {
          userName: User?.registrationNumber,
          otp: data.otp,
          newPassword: encryptedPassword,
        };

        response = isEmployee
          ? await employeeVerifyOtp(payload)
          : await studentVerifyOtp(payload);
      }


      else if (isParent) {
        payload = {
          userName: User.userName || User.email,
          otp: data.otp,
          newPassword: encryptedPassword,
        };

        response = await parentVerifyOtp(payload);
      }


      else {
        payload = {
          userName: user?.email,
          otp: data.otp,
          newPassword: encryptedPassword,
        };

        response = await adminVerifyOtp(payload);
      }

      if (!response?.success) {
        showSnackbar(response?.message || t('header.messages.invalidOtp'), "error");
        return;
      }

      showSnackbar(response?.message || t('header.messages.passwordChangedSuccessfully2'), "success");
      onClose();
    } catch (error) {
      showSnackbar(error.response?.message || t('header.messages.otpVerificationFailed'), "error");
    }
  };

  return (
    <Box px={2} py={1}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h6" color={theme.palette.custom.tableHeadingText} mb={1} className="card-title">
          {t('header.verifyOtp')}
        </Typography>
        <Typography variant="body2" color={theme.palette.custom.tableHeadingText}>
          {t('header.enterTheOtpSentToYour')}
        </Typography>
      </Box>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box
          sx={{
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* OTP Field */}
          <Controller
            name="otp"
            control={control}
            render={({ field }) => (
              <CustomeFormInputField
                {...field}
                label={t('header.otp')}
                size="small"
                placeholder={t('header.enter6DigitsOtp')}
                fullWidth
                required
                error={!!errors.otp}
                helperText={errors.otp?.message}
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  onInput: (e) =>
                    (e.target.value = e.target.value.replace(/\D/g, "")),
                }}
              />
            )}
          />

          {/* Button */}
          <Button
            variant="contained"
            fullWidth
            bgColor={theme.palette.background.bgpurple}
            textColor={theme.palette.custom.whitetext}
            padding="12px 10px"
            fontSize="16px"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? t('common.verifying') : t('header.verifyOtp')}
          </Button>
        </Box>
      </form>

    </Box>
  );
};

export default PasswordOtpVerify;
